import { motion } from 'framer-motion';
import Link from 'next/link';

/**
 * ProblemSection — short agitation block between Hero and Solution Overview.
 * Eight disconnected tool cards visually reinforce "none of them talk".
 *
 * Pain copy is capped at ≤ 12 words per card. Cost numbers stay — they make
 * the pain feel measured. Top + bottom end-grain texture strips act as
 * tactile dividers from the surrounding sections.
 */
export function ProblemSection({ isDarkMode }) {
  const d = isDarkMode;

  const tools = [
    {
      label: 'Website',
      pain: 'A contact form. You email specs back. Most never reply.',
      cost: '10× fewer leads after 1 hr',
    },
    {
      label: 'Quoting',
      pain: 'Excel quote. Forgot the hinges. There goes the margin.',
      cost: 'Same job, 2–3× bid spread',
    },
    {
      label: 'Payments',
      pain: 'Check for the deposit. Chase the balance for a week.',
      cost: '30–45 days to get paid',
    },
    {
      label: 'Orders',
      pain: 'Email → cut sheet → CNC — 34″ becomes 43″.',
      cost: '~1% of fields mistyped',
    },
    {
      label: 'Production',
      pain: 'Whiteboard in the shop. Two guys built the same cabinet.',
      cost: 'Rework: 15–20% of sales',
    },
    {
      label: 'Inventory',
      pain: 'Promised walnut Friday. Four boards on hand, not twelve.',
      cost: 'Manual counts: 1 in 3 off',
    },
    {
      label: 'Shipping',
      pain: 'Quote said $40. Label was $87. You ate the difference.',
      cost: 'Label: +15–20% over quote',
    },
    {
      label: 'Notifications',
      pain: '“Where’s my order?” Twenty calls a week.',
      cost: '20+ status calls/week',
    },
  ];

  return (
    <section
      id='problem'
      className={`relative overflow-hidden ${
        d
          ? 'bg-gradient-to-b from-zinc-950 via-zinc-900/80 to-zinc-950'
          : 'bg-gradient-to-b from-white via-gray-50/80 to-white'
      }`}
    >
      <div className='tc-container max-w-6xl relative z-10 py-20 md:py-28'>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className={`text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight font-display mb-4 text-center [text-wrap:balance] ${
            d ? 'text-white' : 'text-gray-900'
          }`}
        >
          Eight tools.{' '}
          <span className={d ? 'text-jade-soft' : 'text-jade-deep'}>
            None of them interface.
          </span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          viewport={{ once: true }}
          className={`text-base md:text-lg text-center mb-12 md:mb-14 max-w-2xl mx-auto ${
            d ? 'text-zinc-400' : 'text-gray-500'
          }`}
        >
          <span className={d ? 'text-zinc-200' : 'text-gray-800'}>
            Here&rsquo;s what that costs you.
          </span>
        </motion.p>

        {/* Eight disconnected "tool" cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14 md:mb-16'>
          {tools.map((tool, i) => (
            <motion.div
              key={tool.label}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              viewport={{ once: true }}
              className={`relative rounded-xl border p-5 md:p-6 ${
                d
                  ? 'bg-zinc-900/60 border-zinc-800'
                  : 'bg-white border-gray-200'
              }`}
            >
              {/* "disconnected" corner mark */}
              <span
                className={`absolute top-3 right-3 text-[10px] font-sans tracking-widest uppercase ${
                  d ? 'text-zinc-700' : 'text-gray-300'
                }`}
                aria-hidden='true'
              >
                ⌀
              </span>
              <div
                className={`text-[10px] font-bold tracking-[0.2em] uppercase mb-2 ${
                  d ? 'text-zinc-500' : 'text-gray-400'
                }`}
              >
                {tool.label}
              </div>
              <p
                className={`text-base md:text-[17px] leading-snug ${
                  d ? 'text-zinc-200' : 'text-gray-800'
                }`}
              >
                {tool.pain}
              </p>
              <div
                className={`mt-4 pt-3 border-t ${
                  d ? 'border-zinc-800' : 'border-gray-100'
                }`}
              >
                <div
                  className={`text-[11px] md:text-xs font-sans tracking-tight text-right ${
                    d ? 'text-amber-400/90' : 'text-amber-700'
                  }`}
                >
                  {tool.cost}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Closer — links into /how-it-works */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          viewport={{ once: true }}
          className='text-center'
        >
          <div
            className={`mx-auto mb-6 h-px w-16 ${d ? 'bg-zinc-800' : 'bg-gray-200'}`}
          />
          <p
            className={`text-sm md:text-base italic mb-3 ${
              d ? 'text-zinc-400' : 'text-gray-500'
            }`}
          >
            Sound familiar?
          </p>
          <Link
            href='/how-it-works'
            className={`group inline-flex items-baseline gap-3 text-xl md:text-2xl lg:text-3xl font-bold tracking-tight ${
              d ? 'text-white hover:text-jade-soft' : 'text-gray-900 hover:text-jade-deep'
            } transition-colors`}
          >
            That&rsquo;s where{' '}
            <span className={d ? 'text-amber-400' : 'text-amber-600'}>
              up to 40% of your day
            </span>{' '}
            goes.
            <svg
              className='h-5 w-5 self-center transition-transform group-hover:translate-x-1'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M14 5l7 7m0 0l-7 7m7-7H3'
              />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
